import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const alertVariants = cva(
  'relative flex w-full items-start rounded-lg border border-solid',
  {
    variants: {
      variant: {
        info: 'border-blue-600/20 bg-blue-600/5 text-blue-800',
        success: 'border-green-600/20 bg-green-600/5 text-green-800',
        warning: 'border-amber-600/20 bg-amber-600/5 text-amber-800',
        error: 'border-red-600/20 bg-red-600/5 text-red-800',
      },
      // ponytail: md keeps mb-4 from the legacy component — call sites rely
      // on it for spacing; move spacing to the owners before removing it here
      size: {
        md: 'gap-3 p-4 mb-4 text-sm',
        // Inside dense content (a chat answer): no outer margin.
        sm: 'gap-2 px-3 py-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'info',
      size: 'md',
    },
  },
);

type KoobAlertProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'title'
> &
  VariantProps<typeof alertVariants> & {
    /** FontAwesome classes (e.g. "fa-info-circle") or any ReactNode */
    icon?: React.ReactNode;
    title?: React.ReactNode;
    /** Ignored when children are provided */
    description?: React.ReactNode;
    /** @deprecated alerts are always full-width now */
    block?: boolean;
  };

const KoobAlert = React.forwardRef<HTMLDivElement, KoobAlertProps>(
  (
    { icon, title, description, children, variant, size, block, ...props },
    ref,
  ) => {
    const content = children || description;
    // One text line high, so the icon sits on the first line.
    const lineHeight = size === 'sm' ? 'h-4 leading-4' : 'h-5 leading-5';

    return (
      <div
        ref={ref}
        role="alert"
        className={alertVariants({ variant, size })}
        {...props}
      >
        {icon && (
          <div className={`flex ${lineHeight} flex-shrink-0 items-center`}>
            {typeof icon === 'string' ? (
              <i className={icon.includes(' ') ? icon : `fa-regular ${icon}`} />
            ) : (
              icon
            )}
          </div>
        )}
        <div className="flex-1 space-y-1">
          {title && (
            <h3 className={`font-medium ${lineHeight} tracking-tight`}>
              {title}
            </h3>
          )}
          {content && <div className="opacity-90">{content}</div>}
        </div>
      </div>
    );
  },
);

KoobAlert.displayName = 'KoobAlert';

export default KoobAlert;
